import frontendUtils from 'honda_cars_ecux/utils/frontendUtils';

/**
 * Sub-controller for the shared "Showroom Locator" flyout view.
 *
 * Single picker reused by every entry-point that needs to change the user's preferred Honda
 * Centre (`main-menu`, `apply-for-finance-form`, `view-brochure`, …). Branches on the
 * `previousView` captured from `amb:modal-view-change`:
 *
 *   - **Flow A** (`main-menu`): proxies the click to the hidden `#configurator-location-confirm`
 *     input so the legacy `configurator.js#actionLocationConfirm` handler runs. Modal stays
 *     on the locator.
 *   - **Flow B** (any other source): dispatches `amb:showroom-locator:showroom-changed` so
 *     `selectedHondaCentre` blocks in the source view refresh; navigation pops back to source
 *     via the button's `data-view="back"`.
 *
 * Dealer-list AJAX is owned by `selectLocationAndDealerModal.js#stores:getDealers`.
 */
export const showroomLocator = {
  /**
   * Bootstrap the showroom locator view. Idempotent; safe to call once at boot.
   */
  showroomLocatorView() {
    this.helpers.bootstrap();
  },

  /**
   * Controller / state container. Skipped by `processInclude` (not a function).
   */
  helpers: {
    /**
     * The view the user navigated FROM. Drives the Flow A vs Flow B branch in
     * {@link onConfirmButtonClick}.
     *
     * @type {string | null}
     */
    previousView: null,

    /**
     * Place selected in this view open. `location:afterUpdate` only carries `cookieID`;
     * the summary lives on `tempuserloc`. Cleared on entry so a leftover cookie cannot
     * enable Confirm.
     *
     * @private
     *
     * @type {Object|null}
     */
    newLocation: null,

    /**
     * @type {boolean}
     */
    isWired: false,

    /**
     * @type {{
     *   view: HTMLElement | null,
     *   confirmButton: HTMLButtonElement | null,
     *   dealersList: HTMLElement | null,
     *   configuratorConfirm: HTMLInputElement | null,
     * }}
     */
    elements: {
      view: null,
      locationInput: null,
      confirmButton: null,
      dealersList: null,
      configuratorConfirm: null
    },

    /**
     * Subscribe to `amb:modal-view-change` for this view. Captures `previousView` and
     * lazy-wires DOM listeners on first appearance.
     */
    bootstrap() {
      document.body.addEventListener('amb:modal-view-change', event => {
        const {view, previousView, history} = event.detail || {};

        if (view !== 'showroom-locator') {
          return;
        }

        this.previousView = previousView || null;
        this.newLocation = null;
        this.wire();
      });
    },

    /**
     * Resolve element refs and attach the confirm-click + dealer-selection listeners.
     *
     * @returns {boolean} `true` once wiring has succeeded.
     */
    wire() {
      if (this.isWired) {
        this.syncConfirmDisabled();

        return true;
      }

      const view = document.querySelector('.configurator-modal .modal-view[view="showroom-locator"]');

      if (!view) {
        return false;
      }

      const locationInput = view.querySelector('.enter-location-textbox');
      const confirmButton = view.querySelector('.confirm-btn');
      const dealersList = view.querySelector('#configurator-location-dealers');

      if (!locationInput || !confirmButton || !dealersList) {
        return false;
      }

      this.elements.view = view;
      this.elements.locationInput = locationInput;
      this.elements.confirmButton = confirmButton;
      this.elements.dealersList = dealersList;
      this.elements.configuratorConfirm = view.querySelector('#configurator-location-confirm');

      confirmButton.addEventListener('click', event => this.onConfirmButtonClick(event));
      view.addEventListener('change', event => this.onDealerSelectionChange(event));

      $('body').on('location:afterUpdate', (...args) => this.onLocationChange(...args));
      $(dealersList).on('stores:changeDealers', () => this.onDealersChange());

      this.syncConfirmDisabled();
      this.isWired = true;

      return true;
    },

    /**
     * Confirm-button handler — branches on {@link showroomLocator.helpers.previousView}:
     *
     *   - Flow A (`main-menu`): proxies the click to `#configurator-location-confirm` and
     *     `stopPropagation`s so the bubble-phase nav listener doesn't pop history.
     *   - Flow B (any other source): dispatches `amb:showroom-locator:showroom-changed` with
     *     the `:checked` dealer's dataset; nav listener pops back to the source.
     *
     * @param {MouseEvent} event
     */
    onConfirmButtonClick(event) {
      if (this.previousView === 'main-menu') {
        event.stopPropagation();
        $(this.elements.configuratorConfirm).trigger('click');
      } else {
        const selectedShowroom = this.elements.dealersList?.querySelector('input[name="user-location-dealers"]:checked');

        if (!selectedShowroom) {
          return;
        }

        this.elements.view.dispatchEvent(new CustomEvent('amb:showroom-locator:showroom-changed', {
          bubbles: true,
          cancelable: false,
          detail: { ...selectedShowroom.dataset }
        }));
      }
    },

    onLocationChange(event, data) {
      const cookieID = data?.cookieID || 'userloc';

      if (cookieID === 'tempuserloc') {
        this.newLocation = this.readLocationCookie(cookieID);
      }

      this.syncConfirmDisabled();
    },

    onDealersChange() {
      const {view, dealersList} = this.elements;
      let dealerId = null;

      if (this.previousView === 'main-menu') {
        const userLocCookie = frontendUtils.getCookie('userloc');
        const userLocation = userLocCookie ? JSON.parse(userLocCookie) : {};

        dealerId = userLocation.dealer?.dealerId;
      } else {
        const previousView = view.parentElement.querySelector(`.modal-view[view="${this.previousView}"]`);

        dealerId = previousView?.querySelector('.selected-honda-centre input[name="DealerId"]')?.value;
      }

      const userSelectedShowroom = dealersList.querySelector(`input[name="user-location-dealers"][data-dealerid="${dealerId}"]`);

      if (userSelectedShowroom) {
        userSelectedShowroom.checked = true;
      }

      this.syncConfirmDisabled();
    },

    /**
     * `change` delegate on the view — re-sync the confirm-disabled state when the user
     * picks a different dealer card. Delegated on the VIEW so it survives
     * `stores:getDealers` re-renders.
     *
     * @param {Event} event
     */
    onDealerSelectionChange(event) {
      if (!event.target.matches('input[name="user-location-dealers"]')) {
        return;
      }

      this.syncConfirmDisabled();
    },

    /**
     * Toggle the confirm button's `disabled` when the address field is empty, no dealer
     * radio is `:checked`, or (main-menu flow) both the dealer and the pending location
     * summary match the saved `userloc`.
     */
    syncConfirmDisabled() {
      const {view, dealersList, locationInput, confirmButton} = this.elements;
      const selectedLocation = dealersList?.querySelector('input[name="user-location-dealers"]:checked');
      const isDealerView = !!view.closest('.configurator-modal.is-dealer-view');

      dealersList.classList.toggle('d-none', isDealerView && (!this.previousView || this.previousView === 'main-menu'));

      if (this.previousView === 'main-menu') {
        const userLocation = this.readLocationCookie('userloc');
        const isSameDealer = userLocation.dealer?.dealerId === selectedLocation?.dataset.dealerid;
        const isSummaryChanged = !!(this.newLocation?.summary && this.newLocation.summary !== userLocation.summary);

        confirmButton.disabled = isDealerView ? !locationInput.value.trim() : (!selectedLocation || (isSameDealer && !isSummaryChanged));
      } else {
        const previousView = this.elements.view.parentElement.querySelector(`.modal-view[view="${this.previousView}"]`);
        const dealerId = previousView?.querySelector('.selected-honda-centre input[name="DealerId"]')?.value;

        confirmButton.disabled = !selectedLocation || dealerId === selectedLocation?.dataset.dealerid;
      }
    },

    /**
     * @private
     *
     * @param {string} cookieID
     *
     * @returns {Object}
     */
    readLocationCookie(cookieID) {
      const raw = frontendUtils.getCookie(cookieID);

      return raw ? JSON.parse(raw) : {};
    }
  }
};
